import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, Lock, Eye, Search, Users, Award, Globe, CheckCircle, ArrowRight } from 'lucide-react';
import SEOContent from '@/components/SEOContent';

const Home = () => {
  const { t } = useTranslation();
  const services = [
    {
      icon: <Search className="w-8 h-8" />,
      title: t('services.pentest'),
      description: t('services.pentestDesc'),
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: t('services.siem'),
      description: t('services.siemDesc'),
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: t('services.idsips'),
      description: t('services.idsipsDesc'),
    },
    {
      icon: <Search className="w-8 h-8" />,
      title: t('services.vuln'),
      description: t('services.vulnDesc'),
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: t('services.training'),
      description: t('services.trainingDesc'),
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: t('services.malware'),
      description: t('services.malwareDesc'),
    },
  ];

  const certifications = [
    { name: 'ISO 27001', description: 'Information Security Management' },
    { name: 'TSE Integration', description: 'Turkish Standards Institute' },
    { name: 'PCI DSS', description: 'Payment Card Industry Data Security' },
    { name: 'GDPR', description: 'General Data Protection Regulation' },
  ];

  const partners = [
    'Palo Alto Networks',
    'CrowdStrike',
    'Check Point',
    'Fortinet',
    'Cisco Security',
    'Microsoft Security',
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEOContent />
      <Helmet>
        <title>SULH Siber Güvenlik | Penetrasyon Testi, SOC, Red Team | www.sulh.tech</title>
        <meta name="description" content="Türkiye'nin lider siber güvenlik firması. Penetrasyon testi, sızma testi, red team, SOC, SIEM, zafiyet analizi. ISO 27001 sertifikalı, TSE entegre güvenlik hizmetleri." />
        <meta name="keywords" content="siber güvenlik, penetrasyon testi, sızma testi, red team, SOC, SIEM, internet güvenliği" />
        <link rel="canonical" href="https://www.sulh.tech" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-transparent to-cyan-900/20 pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="hero-title">
              {t('hero.title')}
              <br />
              <span className="text-blue-400">{t('hero.titleHighlight')}</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-testid="hero-subtitle">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact">
                <button className="btn-primary" data-testid="hero-cta-primary">
                  {t('hero.cta1')}
                </button>
              </Link>
              <Link to="/services">
                <button className="btn-secondary" data-testid="hero-cta-secondary">
                  {t('hero.cta2')}
                </button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
            <div className="text-center" data-testid="stat-clients">
              <div className="text-4xl font-bold text-blue-400 mb-2">500+</div>
              <div className="text-gray-400 text-sm">{t('stats.clients')}</div>
            </div>
            <div className="text-center" data-testid="stat-threats">
              <div className="text-4xl font-bold text-blue-400 mb-2">99.9%</div>
              <div className="text-gray-400 text-sm">{t('stats.detection')}</div>
            </div>
            <div className="text-center" data-testid="stat-countries">
              <div className="text-4xl font-bold text-blue-400 mb-2">25+</div>
              <div className="text-gray-400 text-sm">{t('stats.countries')}</div>
            </div>
            <div className="text-center" data-testid="stat-uptime">
              <div className="text-4xl font-bold text-blue-400 mb-2">24/7</div>
              <div className="text-gray-400 text-sm">{t('stats.monitoring')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-slate-900/50" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" data-testid="about-title">
                {t('about.title')}
              </h2>
              <p className="text-gray-300 mb-6" data-testid="about-description">
                {t('about.desc1')}
              </p>
              <p className="text-gray-300 mb-6">
                {t('about.desc2')}
              </p>
              <p className="text-gray-300 mb-6">
                {t('about.desc3')}
              </p>
              <Link to="/contact">
                <button className="btn-primary flex items-center gap-2" data-testid="about-cta">
                  {t('about.cta')}
                  <ArrowRight size={18} />
                </button>
              </Link>
            </div>
            <div className="relative">
              <div className="glass-card p-8">
                <Shield className="w-16 h-16 text-blue-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-4">{t('about.commitment')}</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                    <span>{t('about.cert1')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                    <span>{t('about.cert2')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                    <span>{t('about.cert3')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                    <span>{t('about.cert4')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                    <span>{t('about.cert5')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="services-title">
              {t('services.title')}
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="glass-card p-8"
                data-testid={`service-card-${index}`}
              >
                <div className="text-blue-400 mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                <p className="text-gray-400">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/penetration-testing">
              <button className="btn-secondary" data-testid="services-cta">
                {t('services.learnMore')}
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="section-padding bg-slate-900/50" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="certifications-title">
              Compliance & Certifications
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Meeting the highest international standards for cybersecurity and data protection
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="glass-card p-6 text-center"
                data-testid={`cert-card-${index}`}
              >
                <Award className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{cert.name}</h3>
                <p className="text-gray-400 text-sm">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Partnerships */}
      <section className="section-padding" data-testid="partnerships-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Globe className="w-16 h-16 text-blue-400 mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="partnerships-title">
              Global Partnerships
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto mb-8">
              We collaborate with world-leading cybersecurity vendors and technology alliances to deliver
              cutting-edge protection for your organization.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {partners.map((partner, index) => (
              <div
                key={index}
                className="glass-card p-6 text-center"
                data-testid={`partner-card-${index}`}
              >
                <p className="text-white font-medium">{partner}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-blue-900/30 to-cyan-900/30" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" data-testid="cta-title">
            Ready to Secure Your Digital Future?
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            Let's discuss how we can protect your organization with our proven cybersecurity solutions.
          </p>
          <Link to="/contact">
            <button className="btn-primary text-lg" data-testid="cta-button">
              Contact Us Today
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
